<script lang="ts">
  import Prose from '../lib/components/Prose.svelte';
  import Math from '../lib/animation/primitives/Math.svelte';

  const formula = 'P(\\text{success}) = \\text{step accuracy}^{\\text{ number of steps}}';
</script>

<section id="introduction" class="section">
  <Prose>
    <header class="header">
      <h2>Why measure the break?</h2>
      <p class="summary">A practical way to compare prompts, agents, and skills without trusting vibes.</p>
    </header>
    <p>The experiment started with $200 of Anthropic credits and a stubborn question: could a prompt make a language model solve a Rubik's Cube? The same pattern kept showing up in arithmetic, logic, and code tracing: the model looked capable until the task crossed an invisible boundary. The failures were not random. They had structure.</p>
    <p>This is not a catalog of Claude's mistakes. The goal is to make the boundary measurable. I am building a recursive agent-tuning loop in <a href="https://github.com/adam-s/agent-tuning" target="_blank" rel="noopener noreferrer">agent-tuning</a>, and each iteration needs a concrete signal that says whether a change improved the agent or made it worse. The same recursive approach helped search for stock-trading strategies in <a href="https://github.com/adam-s/alphadidactic" target="_blank" rel="noopener noreferrer">alphadidactic</a> and reconstruct websites with a one-shot agent in <a href="https://github.com/adam-s/intercept" target="_blank" rel="noopener noreferrer">intercept</a>. Without a stable evaluation signal, the loop is only guessing.</p>
    <p>This page is the working notebook for that signal: a method for putting numbers on prompts, agents, and skills so changes can be compared directly.</p>
    <p>Across these experiments, each failure reduced to a bad step. A calculation, a state update, a recalled fact, a routing choice — all of them are operations emitted by the model from learned patterns, and each operation has some chance of being wrong. Chain enough of them together and the chance that every step lands cleanly falls fast.</p>
    <p>I call that quantity <strong>step accuracy</strong>. For an agent, it is the number that decides whether the whole run survives. Once failure is framed this way, agent behavior becomes much easier to forecast:</p>
    <Math tex={formula} displayMode={true} />
    <p>The formula treats steps as independent events. They are not; generation is one continuous stream. But the approximation is good enough to predict aggregate behavior and plan around it, which is the useful part.</p>
  </Prose>
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
  }

  .header {
    margin-bottom: var(--space-lg);
  }

  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }

  .caveat {
    color: var(--ink-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
    margin: var(--space-lg) 0;
    border-left: 3px solid var(--ink-muted);
    padding-left: var(--space-md);
    font-style: italic;
  }
</style>
